import { identity } from "@suse-o11y/identity";
import {
	type RequestStatus,
	type StatusErrorOf,
	type StatusMetaOf,
	type StatusValueOf,
	type SuccessRequestStatus,
	type SuccessStatusOf,
	getRequestStatusMeta,
	getRequestStatusValue,
	idleStatus,
	isAnyValueStatus,
	isSuccessRequestStatus,
	successStatus,
} from "@suse-o11y/request-status";
import { mapObservableValue } from "@suse-o11y/request-status/map-observable-value";
import { pollRequest } from "@suse-o11y/request-status/poll";
import { rehydrateErrorLoadingStatusesWithValue } from "@suse-o11y/request-status/rehydrate-error-loading-statuses-with-value";
import { scanObservableValue } from "@suse-o11y/request-status/scan-observable-value";
import {
	type Observable,
	combineLatest,
	concatMap,
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	merge,
	share,
	startWith,
	switchMap,
	tap,
} from "@suse-o11y/rxjs";
import {
	pluckFirst,
	useObservable,
	useObservableCallback,
	useObservableState,
	useSubscription,
} from "@suse-o11y/rxjs/hooks";
import { skipAfter } from "@suse-o11y/rxjs/skip-after";
import last from "lodash/last";
import noop from "lodash/noop";
import { type MutableRefObject, useRef } from "react";

import type { ApiEventItemsWithTotalAndNextCursor, GetEventsStatusWithNextCursor } from "../../../backend/EventsApi";
import type { Api, GetEventsParams } from "../../../common/openapi";
import type { ParamsMeta } from "../../../generic/request";

const POLL_INTERVAL = 15_000;

export interface UseTimeTravelEventsProps {
	timeTravelParams: GetEventsParams;
	onLiveRequest?: () => void;
	onFetchHistoryRequest?: () => void;
	requestEvents: (params: GetEventsParams) => Observable<GetEventsStatusWithNextCursor>;
}

export type GetEventsBatchesStatus = RequestStatus<
	StatusErrorOf<GetEventsStatusWithNextCursor>,
	Array<StatusValueOf<GetEventsStatusWithNextCursor>>,
	StatusMetaOf<GetEventsStatusWithNextCursor>
>;

export interface UseTimeTravelEventsResult {
	status: GetEventsBatchesStatus;

	lastSuccessStatusRef: MutableRefObject<
		SuccessRequestStatus<Array<ApiEventItemsWithTotalAndNextCursor>, ParamsMeta<GetEventsParams>> | undefined
	>;

	onLoadMore: () => void;
	onJumpToHistory: () => void;
	onPauseLive: () => void;
	onRestartRequest: () => void;
}

type CursorEvents = Api.EventCursor;

export const useTimeTravelEvents = ({
	timeTravelParams,
	onLiveRequest = noop,
	onFetchHistoryRequest = noop,
	requestEvents,
}: UseTimeTravelEventsProps): UseTimeTravelEventsResult => {
	const [onRestartRequest, restartRequestEvents] = useObservableCallback<undefined, undefined, []>(identity);
	const lastSuccessStatusRef = useRef<SuccessStatusOf<GetEventsBatchesStatus>>(undefined);

	const [onLoadMore, loadMoreEvents] = useObservableCallback<CursorEvents | undefined, undefined, []>(identity);

	const travelEvents = useObservable<GetEventsParams, [GetEventsParams]>(
		(e) =>
			combineLatest([e.pipe(pluckFirst, debounceTime(250)), restartRequestEvents.pipe(startWith(undefined))]).pipe(
				pluckFirst,
				share(),
			),
		[timeTravelParams],
	);

	const [onJumpToHistory, jumpToHistoryEvents] = useObservableCallback<undefined, undefined, []>(identity);

	useSubscription(travelEvents, (travelEvent) => {
		if (!travelEvent.body.playHeadTimestampMs) {
			onLiveRequest();
		}
	});

	const historyRequestEvents = useObservable(() =>
		travelEvents.pipe(filter(({ body }) => Boolean(body.playHeadTimestampMs))),
	);

	const liveRequestEvents = useObservable(() => travelEvents.pipe(filter(({ body }) => !body.playHeadTimestampMs)));

	const fetchHistoryTravelEvents = useObservable(() => historyRequestEvents.pipe(skipAfter(jumpToHistoryEvents)));

	useSubscription(fetchHistoryTravelEvents, () => {
		onFetchHistoryRequest();
	});

	const [onPauseLive, pauseLiveEvents] = useObservableCallback<undefined, undefined, []>(identity);

	const [status] = useObservableState<GetEventsBatchesStatus, GetEventsBatchesStatus, true>(() => {
		return timeTravelEvents({
			historyRequestEvents,
			liveRequestEvents,
			requestEvents,
			pauseLiveEvents,
			loadMoreEvents,
			lastSuccessStatusRef,
			jumpToHistoryEvents,
		}).pipe(
			rehydrateErrorLoadingStatusesWithValue(),
			tap((status) => {
				/** Save last successful response to ref, so we can revert back to it when needed */
				if (isSuccessRequestStatus(status)) {
					lastSuccessStatusRef.current = status;
				}
			}),
			startWith(idleStatus<ParamsMeta<GetEventsParams>>()),
		);
	});

	return {
		status,
		lastSuccessStatusRef,
		onLoadMore,
		onJumpToHistory,
		onPauseLive,
		onRestartRequest,
	};
};

interface TimeTravelEventsProps {
	historyRequestEvents: Observable<GetEventsParams>;
	liveRequestEvents: Observable<GetEventsParams>;

	pauseLiveEvents: Observable<undefined>;
	loadMoreEvents: Observable<CursorEvents | undefined>;
	jumpToHistoryEvents: Observable<undefined>;
	lastSuccessStatusRef: MutableRefObject<
		SuccessRequestStatus<Array<ApiEventItemsWithTotalAndNextCursor>, ParamsMeta<GetEventsParams>> | undefined
	>;

	requestEvents: (params: GetEventsParams) => Observable<GetEventsStatusWithNextCursor>;
}

const timeTravelEvents = ({
	historyRequestEvents,
	liveRequestEvents,
	pauseLiveEvents,
	jumpToHistoryEvents,
	loadMoreEvents,
	lastSuccessStatusRef,
	requestEvents,
}: TimeTravelEventsProps): Observable<GetEventsBatchesStatus> => {
	/** There are several ways to jump into the history  */
	const historyTravelRequests: Observable<GetEventsBatchesStatus> = merge(
		/** First way. Just jump into timeline  */
		historyRequestEvents.pipe(
			/** Optimization. Do not trigger history request if time jump was caused by load more or expand trace. We already have this request data  */
			skipAfter(jumpToHistoryEvents),
			switchMap((params) => requestEvents(params)),
			/** We have lazy-load batching data, so we actually need to return array of responses */
			mapObservableValue((value) => [value]),
		),
		/** Second way. Expand trace details. If during expanding it was on the loading status, revert to last success  */
		jumpToHistoryEvents.pipe(map(() => lastSuccessStatusRef.current!)),
		/** Third way. Scrolling down to trace list should also revert to last success response, and fetch next batch  */
		loadMoreEvents.pipe(
			map(() => lastSuccessStatusRef.current!),
			switchMap((status) => {
				/** Gather params from last successful fetch request and transform it to params for next batch fetch */
				const body = getRequestStatusMeta(status)!.params.body;
				/** Get last successful response to concat */
				const eventsResponses = getRequestStatusValue(status) ?? [];
				/** Get cursor for next batch of fetching */
				const cursor = last(eventsResponses)?.nextCursor;
				return requestEvents({
					body: {
						...body,
						cursor,
					},
				}).pipe(
					/** Concat batch with original response */
					mapObservableValue((EventsResponse) => eventsResponses.concat(EventsResponse)),
				);
			}),
		),
	).pipe(
		switchMap((status) => {
			/** Gather params from last successful fetch request and transform it to params for next batch fetch */
			const body = getRequestStatusMeta(status)!.params.body;
			return loadMoreEvents.pipe(
				concatMap((cursor) =>
					requestEvents({
						body: {
							...body,
							cursor,
						},
					}),
				),
				scanObservableValue(
					/** Concat responses from the consecutive fetches */
					(EventsResponses, EventsResponse) => EventsResponses.concat(EventsResponse),
					isAnyValueStatus(status)
						? status
						: successStatus<
								Array<StatusValueOf<GetEventsStatusWithNextCursor>>,
								StatusMetaOf<GetEventsStatusWithNextCursor>
							>([]),
				),
				startWith(status),
			);
		}),
	);

	return merge(
		/** Live mode */
		pollRequest({
			startPoll: liveRequestEvents,
			stopPoll: merge<undefined, undefined>(historyRequestEvents, pauseLiveEvents),
			request: requestEvents,
			ignoreIntermediaryLoadings: true,
			interval: POLL_INTERVAL,
		}).pipe(mapObservableValue((value) => [value])),
		/** Past mode */
		historyTravelRequests,
	).pipe(distinctUntilChanged());
};
